import {
  type FC,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";

import {
  AnalyticsContext,
  type IAnalyticsContextValue,
} from "./AnalyticsContext";
import { useAnalyticsHandle } from "./AnalyticsHandleContext";
import {
  type IAnalyticsContextData,
  type IAnalyticsEventInput,
} from "./payload";

export interface IUseAnalyticsResult {
  trackAnalytics: (input: IAnalyticsEventInput) => void;
  ContextProvider: FC<PropsWithChildren<object>>;
  getTrackingData: () => IAnalyticsContextData;
}

export function useAnalytics(
  localContext?: IAnalyticsContextData,
): IUseAnalyticsResult {
  const { instance } = useAnalyticsHandle();
  const inherited = useContext(AnalyticsContext);

  // The seeded context lives in a ref rather than in any memo's dependencies,
  // so re-seeding (a route change reaching a layout's AnalyticsPageContext)
  // never changes the identity of the provider below. See
  // AnalyticsContext.tsx for why that matters — briefly: a per-render provider
  // component is a new component *type* to React, which remounts the subtree
  // instead of re-rendering it.
  const localRef = useRef(localContext);
  useEffect(() => {
    localRef.current = localContext;
  });

  const getMergedContext = useCallback((): IAnalyticsContextData => {
    const local = localRef.current ?? {};
    const merged: IAnalyticsContextData = { ...inherited.getMergedContext() };

    // Only contribute what this level actually set, so a provider that seeds
    // `jobId` alone does not blank out an ancestor's `pageName`.
    if (local.pageName !== undefined) merged.pageName = local.pageName;
    if (local.jobId !== undefined) merged.jobId = local.jobId;
    if (local.referrerUrl !== undefined) merged.referrerUrl = local.referrerUrl;

    // extraInfo merges key-wise instead of replacing, so a nested provider
    // adding `bill_id` keeps an ancestor's `payment_token`. Inner wins per key.
    if (merged.extraInfo || local.extraInfo) {
      merged.extraInfo = { ...merged.extraInfo, ...local.extraInfo };
    }

    return merged;
  }, [inherited]);

  const contextValue = useMemo<IAnalyticsContextValue>(
    () => ({ getMergedContext }),
    [getMergedContext],
  );

  const trackAnalytics = useCallback(
    (input: IAnalyticsEventInput) => {
      // Read at dispatch time, so a click always sees the current seed even
      // though the context value itself never changed identity.
      void instance?.track(input.eventName, {
        __v3: { input, mergedContext: getMergedContext() },
      });
    },
    [instance, getMergedContext],
  );

  const ContextProvider = useCallback(
    ({ children }: PropsWithChildren<object>) => (
      <AnalyticsContext.Provider value={contextValue}>
        {children}
      </AnalyticsContext.Provider>
    ),
    [contextValue],
  );

  return { trackAnalytics, ContextProvider, getTrackingData: getMergedContext };
}
